import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ManageHolidayPackages() {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [status, setStatus] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1
  });

  const [title, setTitle] = useState("");
  const [destination, setDestination] = useState("");
  const [duration, setDuration] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [carouselImages, setCarouselImages] = useState([]);
  const token = localStorage.getItem("token");

  const fileInputRef = useRef(null);
  const shortDescRef = useRef(null);
  const fullDescRef = useRef(null);
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ["link", "image", "table"],
      ['clean'],

    ],
    table: true,
    clipboard: { matchVisual: false }
  }), []);

  const formats = useMemo(() => [
    'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block', 'list', 'indent', 'script', // Only 'list' is needed
    'direction', 'color', 'background', 'align', 'table', 'link', 'image'
  ], []);
  const handleTableAction = (e, action, targetRef) => {
    e.preventDefault(); // Stop focus from breaking

    // Safely grab the specific editor instance that triggered the button
    const editor = targetRef.current?.getEditor();
    if (!editor) return;

    // Direct access to Quill's active table module for THIS editor
    const tableModule = editor.getModule('table');
    if (!tableModule) {
      toast.error("Table module is not initialized.");
      return;
    }

    // Force the editor to focus so it registers the active cell selection
    editor.focus();

    switch (action) {
      case 'insertRow':
        tableModule.insertRowBelow();
        break;
      case 'insertColumn':
        tableModule.insertColumnRight();
        break;
      case 'deleteRow':
        tableModule.deleteRow();
        break;
      case 'deleteColumn':
        tableModule.deleteColumn();
        break;
      default:
        break;
    }
  };


  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setIsSuccess(false);
        setStatus("");
        setShowSuccessModal(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  // Auto-close modal after successful update
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        closeModal();
        setShowSuccessModal(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  /* ================= FETCH ALL PACKAGES ================= */
  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async (page = 1) => {
    setFetching(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/packages?page=${page}&limit=all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const responseData = await res.json();

      if (responseData.data && Array.isArray(responseData.data)) {
        setPackages(responseData.data);
        setPagination(responseData.pagination);
      } else if (Array.isArray(responseData)) {
        setPackages(responseData);
        setPagination({
          page: 1,
          limit: responseData.length,
          total: responseData.length,
          totalPages: 1
        });
      } else {
        setPackages([]);
      }
    } catch (err) {
      console.error("Error fetching packages:", err);
      setPackages([]);
    } finally {
      setFetching(false);
    }
  };

  /* ================= OPEN MODAL ================= */
  const handleEditClick = (pkg) => {
    setSelectedPackage(pkg);
    setTitle(pkg.title || "");
    setDestination(pkg.destination || "");
    setDuration(pkg.duration || "");
    setShortDescription(pkg.shortDescription || "");
    setDescription(pkg.description || "");
    setImage(null);
    setStatus("");
    setIsSuccess(false);
    setShowSuccessModal(false);
    if (fileInputRef.current) fileInputRef.current.value = "";

    let imagesArray = [];

    if (Array.isArray(pkg.images)) {
      imagesArray = pkg.images;
    } else if (typeof pkg.images === "string") {
      try {
        imagesArray = JSON.parse(pkg.images);
      } catch (err) {
        console.error("Failed to parse pkg.images:", err);
      }
    } else if (pkg.images && typeof pkg.images === "object") {
      imagesArray = [pkg.images];
    }

    setCarouselImages(
      imagesArray.map((img, index) => ({
        id: img.id || `existing-${index}`,
        url: img.url || img,
        file: null,
        caption: img.caption || "",
        toDelete: false,
      }))
    );
  };

  const handleDeletePackage = async (pkgId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this package? This will permanently remove all images too."
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/packages/${pkgId}`, {
        method: "DELETE",
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      // Show success feedback with toast
      showToast("✅ Package deleted successfully", "success");
      fetchPackages(pagination.page);
    } catch (err) {
      showToast("❌ " + err.message, "error");
    }
  };

  /* ================= CLOSE MODAL ================= */
  const closeModal = () => {
    setSelectedPackage(null);
    setStatus("");
    setIsSuccess(false);
    setShowSuccessModal(false);
  };

  /* ================= TOAST NOTIFICATION ================= */
  const showToast = (message, type = "success") => {
    setStatus(message);
    setIsSuccess(type === "success");

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setStatus("");
      setIsSuccess(false);
    }, 5000);
  };

  /* ================= UPDATE PACKAGE ================= */
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedPackage) return;

    // Reset status
    setStatus("");
    setIsSuccess(false);
    setShowSuccessModal(false);
    const shortdesc = shortDescription.replace(/&nbsp;/g, " ");
    const fulldesc = description.replace(/&nbsp;/g, " ");

    const formData = new FormData();
    formData.append("title", title);
    formData.append("destination", destination);
    formData.append("duration", duration);
    formData.append("shortDescription", shortdesc);
    formData.append("description", fulldesc);

    // Main image
    if (image) formData.append("image", image);

    // Carousel: separate files and JSON metadata
    const carouselFiles = [];
    const carouselMeta = carouselImages.map((img) => {
      if (img.file) carouselFiles.push(img.file);
      return {
        id: img.id || null,
        caption: img.caption,
        toDelete: img.toDelete,
      };
    });

    carouselFiles.forEach((file) => formData.append("carouselFiles", file));
    formData.append("carouselData", JSON.stringify(carouselMeta));

    try {
      setLoading(true);
      setStatus("⏳ Updating package...");

      const res = await fetch(`${process.env.REACT_APP_API_URL}/packages/${selectedPackage.id}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message);

      // Success!
      setIsSuccess(true);
      setStatus("✅ Package updated successfully!");
      setShowSuccessModal(true);

      // Refresh packages list
      fetchPackages(pagination.page);

      // Close modal after 2 seconds
      setTimeout(() => {
        closeModal();
        setShowSuccessModal(false);
      }, 2000);

    } catch (err) {
      setIsSuccess(false);
      setStatus(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /* ================= CAROUSEL IMAGE HANDLERS ================= */
  const handleCarouselFileChange = (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;

    setCarouselImages((prev) => {
      const newArr = [...prev];
      newArr[idx].file = file;
      newArr[idx].url = URL.createObjectURL(file);
      return newArr;
    });
  };

  const handleCarouselCaptionChange = (idx, caption) => {
    setCarouselImages((prev) => {
      const newArr = [...prev];
      newArr[idx].caption = caption;
      return newArr;
    });
  };

  const handleRemoveCarouselImage = (idx) => {
    setCarouselImages((prev) =>
      prev.map((img, i) =>
        i === idx
          ? img.id
            ? { ...img, toDelete: true }
            : null
          : img
      ).filter(Boolean)
    );
  };

  const handleAddCarouselImage = () => {
    setCarouselImages((prev) => [
      ...prev,
      { file: null, url: null, caption: "", toDelete: false },
    ]);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchPackages(newPage);
    }
  };

  return (
    <>
      {/* ================= TOAST NOTIFICATION ================= */}
      {status && (
        <div
          className={`alert ${isSuccess ? "alert-success" : "alert-danger"} text-center position-fixed top-0 start-50 translate-middle-x mt-3`}
          style={{
            zIndex: 9999,
            minWidth: "300px",
            maxWidth: "500px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            animation: "slideDown 0.5s ease"
          }}
          role="alert"
        >
          <i className={`fa ${isSuccess ? "fa-check-circle" : "fa-exclamation-circle"} me-2`}></i>
          {status}
          {isSuccess && (
            <div className="mt-2">
              <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <small>Closing modal...</small>
            </div>
          )}
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 mt-2 me-2"
            onClick={() => {
              setStatus("");
              setIsSuccess(false);
            }}
          ></button>
        </div>
      )}

      {/* ================= PACKAGE LIST ================= */}
      <div className="container mb-5">
        {fetching ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading packages...</span>
            </div>
            <p className="mt-3">Loading packages...</p>
          </div>
        ) : (
          <>
            {/* Package Stats */}
            <div className="mb-3 text-muted">
              <small>
                Showing {packages.length} of {pagination.total} packages
                {pagination.totalPages > 1 && ` (Page ${pagination.page} of ${pagination.totalPages})`}
              </small>
            </div>

            <div className="row">
              {Array.isArray(packages) && packages.length > 0 ? (
                packages.map((pkg) => (
                  <div className="col-md-4 mb-4" key={pkg.id}>
                    <div className="card shadow-sm h-100">
                      <img
                        src={pkg.image}
                        alt={pkg.title}
                        className="card-img-top"
                        style={{ height: "200px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/600x400?text=No+Image";
                        }}
                      />
                      <div className="card-body">
                        <h5 className="card-title">{pkg.title}</h5>
                        <p className="card-text">
                          <strong>Destination:</strong> {pkg.destination}
                        </p>
                        <p className="card-text">
                          <strong>Duration:</strong> {pkg.duration}
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm navyblue-bg"
                            onClick={() => handleEditClick(pkg)}
                          >
                            <i className="fa fa-edit me-1"></i> Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeletePackage(pkg.id)}
                          >
                            <i className="fa fa-trash me-1"></i> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <div className="alert alert-info text-center">
                    <i className="fa fa-info-circle me-2"></i>
                    No packages found. Create your first package!
                  </div>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Package pagination">
                  <ul className="pagination">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      {/* ================= MODAL ================= */}
      {selectedPackage && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fa fa-edit me-2"></i>
                    Edit Package: {selectedPackage.title}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>

                <div className="modal-body">
                  {/* Status Message inside Modal */}
                  {status && !isSuccess && (
                    <div className={`alert ${isSuccess ? "alert-success" : "alert-danger"} text-center`}>
                      <i className={`fa ${isSuccess ? "fa-check-circle" : "fa-exclamation-circle"} me-2`}></i>
                      {status}
                    </div>
                  )}

                  {isSuccess && (
                    <div className="alert alert-success text-center">
                      <i className="fa fa-check-circle me-2"></i>
                      {status}
                      <div className="mt-2">
                        <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <small>Closing modal...</small>
                      </div>
                    </div>
                  )}

                  {!isSuccess && (
                    <form onSubmit={handleUpdate}>
                      {/* TITLE */}
                      <div className="form-floating mb-3">

                        <input
                          className="form-control"
                          id="title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          required
                          disabled={loading}
                        />
                        <label htmlFor="title">Package Title</label>
                      </div>


                      {/* DESTINATION */}
                      <div className="form-floating mb-3">

                        <input
                          className="form-control"
                          id="destination"
                          value={destination}
                          onChange={(e) => setDestination(e.target.value)}
                          required
                          disabled={loading}
                        />
                        <label htmlFor="destination">Destination</label>
                      </div>

                      {/* DURATION */}
                      <div className="form-floating mb-3">
                        <input
                          className="form-control"
                          id="duration"
                          value={duration}
                          onChange={(e) => setDuration(e.target.value)}
                          required
                          disabled={loading}
                        />
                        <label htmlFor="duration">Duration</label>
                      </div>

                      {/* SHORT DESCRIPTION */}
                      <div className="col-12 my-2 clearfix" style={{ minHeight: "200px" }}>
                        <label className="form-label fw-bold">Short Description</label>
                        <div className="quill-wrapper" style={{ height: "200px", marginBottom: "50px" }}>
                          <div className="mb-2"></div>

                          <div className="btn-group btn-group-sm" role="group">
                            {/* Pass shortDescRef specifically to these actions */}
                            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertRow', shortDescRef)}>
                              ➕ Add Row Below
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertColumn', shortDescRef)}>
                              ➕ Add Column Right
                            </button>
                            <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteRow', shortDescRef)}>
                              ❌ Delete Row
                            </button>
                            <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteColumn', shortDescRef)}>
                              ❌ Delete Col
                            </button>
                          </div>


                          <ReactQuill
                            theme="snow"
                            ref={shortDescRef}
                            formats={formats}
                            modules={modules}
                            value={shortDescription}
                            onChange={setShortDescription}
                            readOnly={loading}
                            style={{ height: "120px", marginBottom: "40px" }}
                          />
                        </div>
                      </div>


                      {/* FULL DESCRIPTION */}
                      <div className="mb-4">
                        <div className="col-12 my-2 clearfix" style={{ minHeight: "300px" }}>
                          <label className="form-label fw-bold">Full Description</label>
                          <div className="quill-wrapper" style={{ height: "300px", marginBottom: "50px" }}>
                            <div className="mb-2">
                              <div className="btn-group btn-group-sm" role="group">
                                {/* Pass fullDescRef specifically to these actions */}
                                <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertRow', fullDescRef)}>
                                  ➕ Add Row Below
                                </button>
                                <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => handleTableAction(e, 'insertColumn', fullDescRef)}>
                                  ➕ Add Column Right
                                </button>
                                <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteRow', fullDescRef)}>
                                  ❌ Delete Row
                                </button>
                                <button type="button" className="btn btn-outline-danger" onMouseDown={(e) => handleTableAction(e, 'deleteColumn', fullDescRef)}>
                                  ❌ Delete Col
                                </button>
                              </div>
                              <ReactQuill
                                theme="snow"
                                ref={fullDescRef}
                                formats={formats}
                                modules={modules}
                                value={description}
                                onChange={setDescription}
                                readOnly={loading}
                                style={{ height: "200px", marginBottom: "50px" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* MAIN IMAGE */}
                      <div className="mb-3">
                        <label className="form-label">Update Main Image (optional)</label>
                        <input
                          type="file"
                          className="form-control"
                          ref={fileInputRef}
                          accept="image/*"
                          onChange={(e) => setImage(e.target.files[0])}
                          disabled={loading}
                        />
                        {selectedPackage.image && !image && (
                          <div className="mt-2">
                            <p className="text-muted small">Current image:</p>
                            <img
                              src={selectedPackage.image}
                              alt="Main preview"
                              className="img-fluid rounded"
                              style={{ maxHeight: "200px" }}
                            />
                          </div>
                        )}
                        {image && (
                          <div className="mt-2">
                            <p className="text-muted small">New image preview:</p>
                            <img
                              src={URL.createObjectURL(image)}
                              alt="Preview"
                              className="img-fluid rounded"
                              style={{ maxHeight: "200px" }}
                            />
                          </div>
                        )}
                      </div>

                      {/* CAROUSEL IMAGES */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold">
                          Carousel Images
                        </label>

                        {carouselImages.map((img, idx) => (
                          <div
                            key={idx}
                            className={`mb-3 border p-2 rounded ${img.toDelete ? "bg-light text-muted" : ""}`}
                          >
                            {!img.toDelete && (
                              <>
                                <input
                                  type="file"
                                  className="form-control mb-2"
                                  accept="image/*"
                                  onChange={(e) => handleCarouselFileChange(e, idx)}
                                  disabled={loading}
                                />

                                {img.url && !img.file && (
                                  <img
                                    src={img.url}
                                    alt="Carousel"
                                    className="img-fluid mb-2 rounded"
                                    style={{ maxHeight: "150px" }}
                                  />
                                )}
                                {img.file && (
                                  <img
                                    src={URL.createObjectURL(img.file)}
                                    alt="Preview"
                                    className="img-fluid mb-2 rounded"
                                    style={{ maxHeight: "150px" }}
                                  />
                                )}
                              </>
                            )}

                            <input
                              type="text"
                              className="form-control mb-2"
                              placeholder="Caption"
                              value={img.caption}
                              onChange={(e) => handleCarouselCaptionChange(idx, e.target.value)}
                              disabled={img.toDelete || loading}
                            />

                            <button
                              type="button"
                              className={`btn btn-sm ${img.toDelete ? "btn-secondary" : "btn-danger"}`}
                              onClick={() => handleRemoveCarouselImage(idx)}
                              disabled={loading}
                            >
                              {img.toDelete ? "Undo Remove" : "Remove"}
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={handleAddCarouselImage}
                          disabled={loading}
                        >
                          + Add Carousel Image
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  {!isSuccess && (
                    <button
                      type="button"
                      className="btn navyblue-bg"
                      onClick={handleUpdate}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-save me-2"></i>
                          Update Package
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )
      }

      {/* Add CSS animation for toast */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default ManageHolidayPackages;